import { apiClient } from '@/lib/apiClient';
import type {
  ApiResponse,
  Ticket,
  TicketCreate,
  TicketUpdate,
  TicketListParams,
  TicketResponse,
  TicketResponseCreate,
  TicketResponseUpdate
} from "dico-types";

const base = (workspaceId: string) => `/api/workspaces/${workspaceId}/tickets`;

const listTickets = (workspaceId: string, params?: TicketListParams, signal?: AbortSignal) => {
  const qs = new URLSearchParams();
  if (params?.statusId) qs.set('statusId', params.statusId);
  if (params?.priorityId) qs.set('priorityId', params.priorityId);
  if (params?.limit) qs.set('limit', String(params.limit));
  if (params?.cursor) qs.set('cursor', params.cursor);
  const query = qs.toString();
  return apiClient.get<ApiResponse<Ticket[]>>(`${base(workspaceId)}${query ? `?${query}` : ''}`, {
    signal
  });
};

const getTicket = (workspaceId: string, ticketId: string, signal?: AbortSignal) =>
  apiClient.get<ApiResponse<Ticket>>(`${base(workspaceId)}/${ticketId}`, { signal });

const createTicket = (
  workspaceId: string,
  data: Omit<TicketCreate, 'workspaceId'>,
  signal?: AbortSignal
) => apiClient.post<ApiResponse<Ticket>>(base(workspaceId), data, { signal });

const updateTicket = (
  workspaceId: string,
  ticketId: string,
  data: TicketUpdate,
  signal?: AbortSignal
) => apiClient.patch<ApiResponse<Ticket>>(`${base(workspaceId)}/${ticketId}`, data, { signal });

const createTicketResponse = (
  workspaceId: string,
  ticketId: string,
  data: Omit<TicketResponseCreate, 'ticketId' | 'workspaceId'>,
  signal?: AbortSignal
) =>
  apiClient.post<ApiResponse<TicketResponse>>(
    `${base(workspaceId)}/${ticketId}/responses`,
    data,
    { signal }
  );

const publishTicketResponse = (
  workspaceId: string,
  ticketId: string,
  responseId: string,
  signal?: AbortSignal
) =>
  apiClient.post<ApiResponse<TicketResponse>>(
    `${base(workspaceId)}/${ticketId}/responses/${responseId}/publish`,
    {},
    { signal }
  );

const updateTicketResponse = (
  workspaceId: string,
  ticketId: string,
  responseId: string,
  data: TicketResponseUpdate,
  signal?: AbortSignal
) =>
  apiClient.patch<ApiResponse<TicketResponse>>(
    `${base(workspaceId)}/${ticketId}/responses/${responseId}`,
    data,
    { signal }
  );

const deleteTicketResponse = (
  workspaceId: string,
  ticketId: string,
  responseId: string,
  signal?: AbortSignal
) =>
  apiClient.delete<ApiResponse<void>>(
    `${base(workspaceId)}/${ticketId}/responses/${responseId}`,
    { signal }
  );

export const ticketApi = {
  listTickets,
  getTicket,
  createTicket,
  updateTicket,
  createTicketResponse,
  updateTicketResponse,
  deleteTicketResponse,
  publishTicketResponse
};

export {
  listTickets,
  getTicket,
  createTicket,
  updateTicket,
  createTicketResponse,
  updateTicketResponse,
  deleteTicketResponse,
  publishTicketResponse
};
